import { useConversation } from '@/services/conversationService';
import { firestore } from '@/lib/firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  Content,
  Conversation,
  Message,
  Model,
  Parameter,
} from '@shared/types';
import type { DesignSpec, SpecParameter } from '@shared/spec';
import { HistoryConversation } from '@/types/misc';
import {
  QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { updateParameter } from '@/utils/parameterUtils';
import { stripUndefined } from '@/utils/stripUndefined';
import { useCallback } from 'react';
import {
  DEFAULT_PARAMETRIC_MODEL,
  DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
} from '@/lib/utils';
import i18n from '@/i18n/i18n';

function messageSentConversationUpdate(
  newMessage: Message,
  conversationId: string,
) {
  return (
    oldConversations: Conversation[] | HistoryConversation[] | undefined,
  ) => {
    if (!oldConversations) return oldConversations;
    return oldConversations
      .map((conv) => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            current_message_leaf_id: newMessage.id,
            updated_at: newMessage.created_at,
          };
        }
        return conv;
      })
      .sort((a: Conversation, b: Conversation) => {
        return (
          new Date(b.updated_at ?? '').getTime() -
          new Date(a.updated_at ?? '').getTime()
        );
      });
  };
}

function messageInsertedConversationUpdate(
  queryClient: QueryClient,
  newMessage: Message,
  conversationId: string,
) {
  // Update the current conversation optimistically
  queryClient.setQueryData(
    ['conversation', conversationId],
    (oldConversation: Conversation) => ({
      ...oldConversation,
      current_message_leaf_id: newMessage.id,
    }),
  );

  // Update messages optimistically
  queryClient.setQueryData(
    ['messages', conversationId],
    (oldMessages: Message[] | undefined) => {
      if (!oldMessages || oldMessages.length === 0) return [newMessage];
      if (oldMessages.find((msg) => msg.id === newMessage.id)) {
        return oldMessages.map((msg) =>
          msg.id === newMessage.id ? newMessage : msg,
        );
      }
      return [...oldMessages, newMessage];
    },
  );

  // Update conversations list optimistically instead of invalidating
  queryClient.setQueryData(
    ['conversations'],
    messageSentConversationUpdate(newMessage, conversationId),
  );

  // Also update the recent conversations in sidebar
  queryClient.setQueryData(
    ['conversations', 'recent'],
    messageSentConversationUpdate(newMessage, conversationId),
  );
}

export const useMessagesQuery = () => {
  const { conversation } = useConversation();
  return useQuery<Message[]>({
    enabled: !!conversation.id,
    queryKey: ['messages', conversation.id],
    initialData: [],
    queryFn: async () => {
      const messagesCol = collection(
        firestore,
        'conversations',
        conversation.id,
        'messages',
      );
      const q = query(messagesCol, orderBy('created_at', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Message[];
    },
  });
};

function useInsertMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      message: Omit<Message, 'id' | 'created_at'>,
    ) => {
      const id = crypto.randomUUID();
      const created_at = new Date().toISOString();
      const newMessage: Message = {
        ...message,
        id,
        created_at,
      };
      const msgRef = doc(
        firestore,
        'conversations',
        message.conversation_id,
        'messages',
        id,
      );
      await setDoc(msgRef, stripUndefined(newMessage));

      // Update conversation leaf
      const convRef = doc(firestore, 'conversations', message.conversation_id);
      await updateDoc(convRef, {
        current_message_leaf_id: id,
        updated_at: created_at,
      });

      return newMessage;
    },
    onSuccess(newMessage) {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        newMessage.conversation_id,
      );
    },
    onError(error) {
      console.error(error);
    },
  });
}

function useParametricChatMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationKey: ['parametric-chat', conversationId],
    mutationFn: async ({
      model,
      messageId,
      conversationId,
      thinking,
      turboMode,
    }: {
      model: Model;
      messageId: string;
      conversationId: string;
      thinking?: boolean;
      turboMode?: boolean;
    }) => {
      const newMessageId = crypto.randomUUID();
      let initialized = false;

      const { chat } = await import('@/services/chatService');

      const finalMessage = await chat({
        conversationId,
        messageId,
        newMessageId,
        model,
        thinking,
        turboMode,
        queryClient,
        onMessage: (data: Message) => {
          // Update streaming message in React Query cache
          queryClient.setQueryData(
            ['messages', conversationId],
            (oldMessages: Message[] | undefined) => {
              if (!oldMessages || oldMessages.length === 0) return [data];
              if (oldMessages.find((msg) => msg.id === data.id)) {
                return oldMessages.map((msg) =>
                  msg.id === data.id ? data : msg,
                );
              }
              return [...oldMessages, data];
            },
          );

          if (!initialized) {
            // Update conversation leaf ID
            queryClient.cancelQueries({
              queryKey: ['conversation', conversationId],
            });
            queryClient.setQueryData(
              ['conversation', conversationId],
              (oldConversation: Conversation) => ({
                ...oldConversation,
                current_message_leaf_id: newMessageId,
              }),
            );
            initialized = true;
          }
        },
      });

      return finalMessage;
    },
    onSuccess: (newMessage) => {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        conversationId,
      );
    },
    onError: async (error, { messageId }) => {
      console.error('Chat error:', error);
      const errorDetail = error instanceof Error ? error.message : String(error);
      try {
        await insertMessageAsync({
          role: 'assistant',
          content: {
            text: `${i18n.t('services.errorPrefix', { ns: 'common' })}${errorDetail}`,
            error: errorDetail,
          },
          parent_message_id: messageId,
          conversation_id: conversationId,
        });
      } catch (insertError) {
        console.error('Failed to insert error message:', insertError);
      }
    },
  });
}

export function useSendContentMutation({
  conversation,
}: {
  conversation: Pick<
    Conversation,
    'id' | 'current_message_leaf_id' | 'user_id'
  >;
}) {
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['send-content', conversation.id],
    mutationFn: async (content: Content & { turboMode?: boolean }) => {
      const userMessage = await insertMessageAsync({
        role: 'user',
        content,
        parent_message_id: conversation.current_message_leaf_id ?? null,
        conversation_id: conversation.id,
      });

      await sendToParametricChat({
        model: DEFAULT_PARAMETRIC_MODEL,
        messageId: userMessage.id,
        conversationId: conversation.id,
        thinking: content.thinking,
        turboMode: content.turboMode,
      });
    },
  });
}

export function useUpdateMessageOptimisticMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ message }: { message: Message }) => {
      const msgRef = doc(
        firestore,
        'conversations',
        message.conversation_id,
        'messages',
        message.id,
      );
      await updateDoc(msgRef, { content: message.content });
      return message;
    },
    onMutate: async ({ message }) => {
      await queryClient.cancelQueries({
        queryKey: ['messages', message.conversation_id],
      });
      const oldMessages = queryClient.getQueryData<Message[]>([
        'messages',
        message.conversation_id,
      ]);
      queryClient.setQueryData(
        ['messages', message.conversation_id],
        oldMessages?.map((msg) =>
          msg.id === message.id ? { ...msg, ...message } : msg,
        ),
      );
      return { oldMessages };
    },
    onSettled(_data, _error, { message }) {
      queryClient.invalidateQueries({
        queryKey: ['messages', message.conversation_id],
      });
    },
    onError(error, { message }, context) {
      console.error(error);
      queryClient.setQueryData(
        ['messages', message.conversation_id],
        context?.oldMessages,
      );
    },
  });
}

export function useEditMessageMutation() {
  const { conversation } = useConversation();

  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['edit-message', conversation.id],
    mutationFn: async (updatedMessage: Message) => {
      const userMessage = await insertMessageAsync({
        role: updatedMessage.role,
        content: updatedMessage.content,
        parent_message_id: updatedMessage.parent_message_id ?? null,
        conversation_id: conversation.id,
      });

      sendToParametricChat({
        model: DEFAULT_PARAMETRIC_MODEL,
        messageId: userMessage.id,
        conversationId: conversation.id,
        thinking: updatedMessage.content.thinking,
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
}


export function useRetryMessageMutation() {
  const { conversation, updateConversationAsync } = useConversation();

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['retry-message', conversation.id],
    mutationFn: async ({ id }: { id: string }) => {
      if (!updateConversationAsync) {
        throw new Error(i18n.t('services.cannotUpdateConversation', { ns: 'common' }));
      }

      await updateConversationAsync({
        ...conversation,
        current_message_leaf_id: id,
      });

      sendToParametricChat({
        model: DEFAULT_PARAMETRIC_MODEL,
        messageId: id,
        conversationId: conversation.id,
        thinking: DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
}

export function useChangeParameters() {
  const { mutate: updateMessageOptimistic } =
    useUpdateMessageOptimisticMutation();
  const queryClient = useQueryClient();
  const { conversation } = useConversation();

  return useCallback(
    (message: Message | null, updatedParameters: Parameter[]) => {
      if (!message) return;

      const newContent = buildParameterUpdatedContent(
        message,
        updatedParameters,
        { dropFiles: true },
      );

      updateMessageOptimistic(
        {
          message: { ...message, content: newContent },
        },
        {
          onError(_error, _variables, context) {
            if (context?.oldMessages) {
              queryClient.setQueryData(
                ['messages', conversation.id],
                context.oldMessages,
              );
            }
          },
        },
      );
    },
    [updateMessageOptimistic, queryClient, conversation.id],
  );
}

export function buildParameterUpdatedContent(
  message: Message,
  updatedParameters: Parameter[],
  options: { dropFiles?: boolean; updateCode?: boolean } = {},
): Content {
  let newCode = message.content.artifact?.code ?? '';
  if (options.updateCode !== false) {
    updatedParameters.forEach((param) => {
      if (param.name.length > 0) {
        newCode = updateParameter(newCode, param);
      }
    });
  }
  const updatedSpec = message.content.artifact?.spec
    ? updateSpecParameters(
        message.content.artifact.spec,
        updatedParameters,
      )
    : undefined;

  return {
    text: message.content.text ?? '',
    model: DEFAULT_PARAMETRIC_MODEL,
    artifact: {
      title: message.content.artifact?.title ?? '',
      version: message.content.artifact?.version ?? '',
      code: newCode,
      parameters: updatedParameters,
      // Persisted parameter commits drop stale files. Live local previews keep
      // them so the viewer can continue showing the old mesh while the latest
      // SCAD compile is debounced in the background.
      files: options.dropFiles === false
        ? (message.content.artifact?.files ?? [])
        : [],
      ...(updatedSpec ? { spec: updatedSpec } : {}),
      ...(message.content.artifact?.drawingMetadata
        ? { drawingMetadata: message.content.artifact.drawingMetadata }
        : {}),
    },
  };
}

function updateSpecParameters(
  spec: DesignSpec,
  updatedParameters: Parameter[],
): DesignSpec {
  const byName = new Map(updatedParameters.map((param) => [param.name, param]));
  return {
    ...spec,
    parameters: spec.parameters.map((specParam) =>
      mergeSpecParameter(specParam, byName.get(specParam.name)),
    ),
  };
}

function mergeSpecParameter(
  specParam: SpecParameter,
  updatedParam: Parameter | undefined,
): SpecParameter {
  if (!updatedParam) return specParam;

  const next: SpecParameter = { ...specParam };
  if (isSpecParameterValue(updatedParam.value)) {
    next.value = updatedParam.value;
  }
  if (isSpecParameterValue(updatedParam.defaultValue)) {
    next.defaultValue = updatedParam.defaultValue;
  }
  next.displayName = updatedParam.displayName;
  if (
    updatedParam.type === 'string' ||
    updatedParam.type === 'number' ||
    updatedParam.type === 'boolean'
  ) {
    next.type = updatedParam.type;
  }
  if (updatedParam.description !== undefined) {
    next.description = updatedParam.description;
  }
  if (updatedParam.group !== undefined) {
    next.group = updatedParam.group;
  }
  if (updatedParam.range !== undefined) {
    next.range = updatedParam.range;
  }
  return next;
}

function isSpecParameterValue(
  value: Parameter['value'],
): value is SpecParameter['value'] {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

export function useIsLoading() {
  const { conversation } = useConversation();
  const isSendingChat = useIsMutating({
    mutationKey: ['parametric-chat', conversation.id],
  });
  const isSendingMessage = useIsMutating({
    mutationKey: ['send-content', conversation.id],
  });
  const isEditingMessage = useIsMutating({
    mutationKey: ['edit-message', conversation.id],
  });
  const isRetryingMessage = useIsMutating({
    mutationKey: ['retry-message', conversation.id],
  });
  const isSending =
    !!isSendingChat ||
    !!isSendingMessage ||
    !!isEditingMessage ||
    !!isRetryingMessage;
  return isSending;
}
